import React, { useState, useEffect, useMemo } from "react";
import { getApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import {
    Activity,
    Database,
    Zap,
    ArrowRight,
    PieChart,
    Calendar,
    ChevronDown,
    ChevronUp,
    Info,
    Layout
} from "lucide-react";

/**
 * QuestionAnswerView - High Fidelity Sustainability Report
 * Redesigned for maximum ergonomics and user friendliness.
 */
const QuestionAnswerView = ({ questions: questionsProp, sectionId, subSectionId }) => {
    const [answers, setAnswers] = useState({});
    const [metadata, setMetadata] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [expandedSections, setExpandedSections] = useState({});

    // 1. RECURSIVE Flattening to get ALL questions (including deeply nested ones like monetary_value_spent)
    const questionsList = useMemo(() => {
        if (!questionsProp) return [];

        const flat = [];
        const flatten = (items) => {
            if (!items || !Array.isArray(items)) return;
            items.forEach(item => {
                if (item.questionId) flat.push(item);
                if (item.questions) flatten(item.questions);
                if (item.divisions) flatten(item.divisions);
            });
        };
        flatten(questionsProp);

        const uniqueMap = new Map();
        flat.forEach(q => {
            const existing = uniqueMap.get(q.questionId);
            if (!existing || (!existing.displayCondition && q.displayCondition)) {
                uniqueMap.set(q.questionId, q);
            }
        });
        return Array.from(uniqueMap.values());
    }, [questionsProp]);

    // 2. State Reconstruction - Maps ALL API keys including calculations
    const reconstructAnswersState = (apiAnswers) => {
        const initialState = {};
        if (!apiAnswers) return initialState;

        apiAnswers.forEach(ans => {
            const { questionId, questionType, value, tableRows, optionValue, tripIndex } = ans;
            const baseObj = { ...ans };

            if (questionType === "table" && tableRows) {
                const tableData = {};
                tableRows.forEach(row => {
                    const itemType = Array.isArray(row.values?.item_type) ? row.values.item_type[0] : "default";
                    if (!tableData[itemType]) tableData[itemType] = [];
                    tableData[itemType].push(row);
                });
                baseObj.tableData = tableData;
                initialState[questionId] = baseObj;
            }
            else if (optionValue || (tripIndex !== null && tripIndex !== undefined)) {
                let key = questionId;
                if (tripIndex !== null && tripIndex !== undefined) key += `_trip_${tripIndex}`;
                if (optionValue) key += `_${optionValue}`;
                initialState[key] = baseObj;
            }
            else {
                initialState[questionId] = baseObj;
            }
        });
        return initialState;
    };

    useEffect(() => {
        const fetchAnswers = async () => {
            setIsLoading(true);
            try {
                const res = await getApi({ apiUrl: `${API_URLS.GET_ANSWERS}?sectionId=${sectionId}&subSectionId=${subSectionId}` });
                const apiData = Array.isArray(res.data) ? res.data[0] : res.data;

                if (res?.success && apiData) {
                    const ansArray = apiData.answers || [];
                    setMetadata({
                        userId: apiData.UserId,
                        formId: apiData.standardFormAllotmentId,
                        updatedAt: apiData.updatedAt,
                        totalSectionEmission: ansArray.reduce((sum, a) => sum + (Number(a.totalEmission) || 0), 0)
                    });
                    setAnswers(reconstructAnswersState(ansArray));
                }
            } catch (err) { console.error("Report Fetch Failed", err); }
            finally { setIsLoading(false); }
        };
        if (sectionId && subSectionId) fetchAnswers();
    }, [sectionId, subSectionId]);

    const getOptionLabel = (qid, val) => {
        if (val === undefined || val === null) return "";

        // If it's the complex Business Travel calculation object, extract the count
        if (typeof val === 'object' && val.hasOwnProperty('number_of_trip')) {
            return String(val.number_of_trip);
        }

        // Find the question definition
        const q = questionsList.find(x => x.questionId === qid);

        // Safety: If val is an object, try to extract a label or name
        if (typeof val === 'object' && !Array.isArray(val)) {
            const possibleLabel = val.label || val.name || val.text || val.key || val.value;
            if (possibleLabel !== undefined) return String(possibleLabel);
            return JSON.stringify(val); // Fallback to stringify to prevent React crash
        }

        // If we don't have a specific question definition, try to find ANY question with this ID to get options
        if (!q || !q.options) {
            return typeof val === 'string' ? val.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : String(val);
        }

        const valToCompare = val;
        const opt = q.options.find(o => o.value === valToCompare || o === valToCompare);
        return opt?.label || opt || valToCompare || val;
    };

    const toggleSection = (id) => {
        setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // --- RENDER HELPERS ---

    const renderValue = (question, ansObj) => {
        let val = ansObj?.value;
        if (val === undefined || val === null || val === "") return <span className="text-gray-300 italic text-xs">No Data</span>;

        // --- GENERIC HANDLERS FOR DIFFERENT API STRUCTURES ---

        // 1. Handle { selectedValues: [...] } or { number_of_trip, details } structure
        if (typeof val === 'object' && !Array.isArray(val)) {
            if (val.selectedValues) {
                val = val.selectedValues;
            } else if (val.hasOwnProperty('number_of_trip')) {
                // Specific fix for Business Travel calculation object
                return (
                    <div className="flex items-center gap-2">
                        <span className="text-gray-900 font-bold text-base bg-gray-50 px-3 py-1 rounded-lg border border-gray-100">
                            {String(val.number_of_trip)}
                        </span>
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Trips Processed</span>
                    </div>
                );
            }
        }

        // 2. Handle [ { key: '...', value: '...' } ] structure
        if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object' && (val[0].key !== undefined || val[0].value !== undefined)) {
            return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                    {val.map((item, i) => (
                        <div key={i} className="flex justify-between items-center p-3 bg-gray-50/50 rounded-xl border border-gray-100 hover:border-teal-100 transition-colors">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-tight pr-4">
                                {getOptionLabel(null, item.key || item.value)}
                            </span>
                            <span className="text-sm font-black text-teal-700 tabular-nums">
                                {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        // 3. Standard Array or Single Value
        const isArray = Array.isArray(val);
        return (
            <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                    {isArray ? val.map((v, i) => (
                        <span key={i} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-lg text-[11px] font-bold border border-teal-100 uppercase tracking-tight shadow-sm">
                            {getOptionLabel(question?.questionId, v)}
                        </span>
                    )) : (
                        <span className="text-gray-900 font-bold text-base bg-gray-50 px-3 py-1 rounded-lg border border-gray-100">
                            {getOptionLabel(question?.questionId, val)}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderTable = (q, forcedKey) => {
        const key = forcedKey || q.questionId;
        const fullAns = answers[key] || {};
        const tableData = fullAns.tableData || {};

        if (Object.keys(tableData).length === 0) return <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-center text-gray-400 italic text-xs">No entries submitted.</div>;

        return Object.keys(tableData).map(catKey => {
            const rows = tableData[catKey] || [];
            if (rows.length === 0) return null;

            const headers = new Set();
            rows.forEach(row => {
                Object.keys(row.values || {}).forEach(k => headers.add(k));
                if (row.calculatedEmission !== undefined) headers.add('impact');
            });

            const sortedHeaders = Array.from(headers);
            const finalHeaders = sortedHeaders.filter(h => h !== 'item_type' && h !== 'impact');
            if (headers.has('item_type')) finalHeaders.unshift('item_type');
            if (headers.has('impact')) finalHeaders.push('impact');

            return (
                <div key={catKey} className="mt-4 mb-6 border border-gray-100 rounded-2xl overflow-hidden shadow-sm bg-white">
                    <div className="bg-gray-50/80 px-4 py-2 flex justify-between items-center border-b border-gray-100">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{catKey === 'default' ? 'DATA ENTRIES' : catKey}</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-white/50">
                                    {finalHeaders.map(h => (
                                        <th key={h} className="px-4 py-3 text-[9px] font-black text-gray-400 uppercase tracking-widest text-left border-b border-gray-50">
                                            {h === 'impact' ? 'Calculated kgCO₂e' : h.replace(/_/g, ' ')}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-teal-50/10 border-b border-gray-50 last:border-0 transition-colors">
                                        {finalHeaders.map(h => {
                                            let val = h === 'impact' ? row.calculatedEmission : row.values?.[h];
                                            let display = "—";

                                            if (val !== undefined && val !== null && val !== "") {
                                                if (Array.isArray(val)) {
                                                    display = (
                                                        <div className="flex flex-wrap gap-1">
                                                            {val.map((item, i) => (
                                                                <span key={i} className="px-1.5 py-0.5 bg-gray-50 text-[9px] font-bold text-gray-500 rounded border border-gray-100">
                                                                    {typeof item === 'object' ? `${item.key}: ${item.value}` : String(item)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    );
                                                } else if (h === 'impact') {
                                                    display = <span className="font-mono text-[13px] font-black text-teal-600 tabular-nums">{Number(val).toLocaleString()}</span>;
                                                } else {
                                                    display = <span className="text-gray-700 font-medium text-xs font-mono">{String(val)}</span>;
                                                }
                                            }
                                            return <td key={h} className="px-4 py-3 align-middle">{display}</td>;
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        });
    };

    if (isLoading) return (
        <div className="flex flex-col items-center justify-center h-[400px]">
            <div className="w-10 h-10 border-4 border-teal-100 border-t-teal-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Generating Analysis...</p>
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto px-4 py-2 space-y-6 animate-in fade-in duration-500">

            {/* 1. STICKY SUMMARY STRIP */}
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md rounded-2xl border border-teal-100 shadow-lg px-8 py-4 flex justify-between items-center -mx-4 mb-8">
                <div className="flex items-center gap-4">
                    <div className="p-2.5 bg-teal-600 rounded-xl shadow-lg shadow-teal-200">
                        <Zap size={18} className="text-white" />
                    </div>
                    <div>
                        <h2 className="text-base font-black text-gray-900 uppercase tracking-tight leading-none">Emissions Summary</h2>
                        <p className="text-[10px] text-gray-400 font-bold mt-1.5 uppercase tracking-widest flex items-center gap-2">
                            <Layout size={10} /> {subSectionId.replace(/_/g, ' ')}
                        </p>
                    </div>
                </div>

                {/* <div className="flex items-center gap-8">
                    <div className="text-right border-r border-gray-100 pr-8">
                        <span className="block text-[9px] text-gray-400 font-black uppercase tracking-widest mb-1.5">Section Total Impact</span>
                        <div className="flex items-baseline gap-1.5">
                            <span className="text-2xl font-black text-teal-600 tracking-tighter">{(metadata.totalSectionEmission || 0).toFixed(3)}</span>
                            <span className="text-[10px] font-black text-teal-800/40 uppercase">kgCO₂e</span>
                        </div>
                    </div>
                </div> */}
            </div>

            {/* 2. DATA GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
                {/* 1st Pass: Render regular questions from the spec */}
                {questionsList.map((q) => {
                    const ansObj = answers[q.questionId];
                    const isTable = q.questionType === 'table';
                    const isExpanded = expandedSections[q.questionId] !== false;

                    if (!ansObj && !isTable) return null;

                    return (
                        <div key={q.questionId}
                            className={`group bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-teal-100 ${isTable ? 'col-span-1' : ''}`}>
                            <QuestionCard
                                q={q}
                                ansObj={ansObj}
                                isTable={isTable}
                                isExpanded={isExpanded}
                                isUnmapped={false}
                                onToggle={() => toggleSection(q.questionId)}
                                renderTable={renderTable}
                                renderValue={renderValue}
                            />
                        </div>
                    );
                })}

                {/* 2nd Pass: Render answers in API that are NOT in the question list (Unmapped/Dynamic data) */}
                {Object.keys(answers).filter(id => !questionsList.find(q => q.questionId === id)).map((id) => {
                    const ansObj = answers[id];
                    const isExpanded = expandedSections[id] !== false;

                    return (
                        <div key={id} className="group bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden border-l-4 border-l-teal-400">
                            <QuestionCard
                                q={{ questionId: id, questionText: id.replace(/_trip_undefined/g, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }}
                                ansObj={ansObj}
                                isTable={ansObj.questionType === 'table'}
                                isExpanded={isExpanded}
                                isUnmapped={true}
                                onToggle={() => toggleSection(id)}
                                renderTable={renderTable}
                                renderValue={renderValue}
                            />
                        </div>
                    );
                })}
            </div>

            {/* 3. REPORT FOOTER */}
            <div className="pt-12 pb-6 flex flex-col items-center gap-4 text-center">
                <div className="flex items-center gap-12 grayscale opacity-30">
                    <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg"><span className="text-[9px] font-black text-gray-500">USER: {metadata.userId?.slice(-6).toUpperCase()}</span></div>
                    <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg"><span className="text-[9px] font-black text-gray-500">FORM: {metadata.formId?.slice(-6).toUpperCase()}</span></div>
                </div>
                <p className="text-[9px] font-black text-gray-300 uppercase tracking-[0.4em] select-none">Sustainability Audit Powered by Carburn</p>
            </div>
        </div>
    );
};

const QuestionCard = ({ q, ansObj, isTable, isExpanded, isUnmapped, onToggle, renderTable, renderValue }) => (
    <>
        <div className="px-6 py-4 flex items-center justify-between cursor-pointer active:bg-gray-50" onClick={onToggle}>
            <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isTable ? 'bg-teal-50 text-teal-600' : 'bg-gray-50 text-gray-400 group-hover:bg-teal-50 group-hover:text-teal-600'}`}>
                    {isTable ? <Database size={14} /> : <FileText size={14} />}
                </div>
                <div>
                    <h5 className="text-[13px] font-bold text-gray-800 line-clamp-1">{q.questionText}</h5>
                    {!isTable && (
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-0.5">
                            {isUnmapped ? 'Dynamic Response' : 'Input Answer'}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-4">
                {ansObj?.totalEmission > 0 && (
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-teal-50/50 rounded-lg border border-teal-100/50">
                        <Zap size={10} className="text-teal-600" />
                        <span className="font-mono text-[15px] font-black text-teal-600">{ansObj.totalEmission.toLocaleString()} <span className="text-[8px] opacity-70">kgCO2e</span></span>
                    </div>
                )}
                <div className="text-gray-300 group-hover:text-teal-400 transition-colors">
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
            </div>
        </div>

        {isExpanded && (
            <div className="px-6 pb-6 pt-2 border-t border-gray-50/50 animate-in fade-in slide-in-from-top-2 duration-300">
                {isTable ? renderTable(q) : renderValue(q, ansObj)}
                {ansObj?.appliedFactors?.length > 0 && (
                    <div className="mt-4 flex items-start gap-2 bg-gray-50 p-3 rounded-xl border border-gray-100">
                        <Info size={14} className="text-gray-300 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                            <span className="block text-[9px] font-black text-gray-400 uppercase tracking-widest">Calculation Methodology</span>
                            <div className="flex flex-wrap gap-1">
                                {ansObj.appliedFactors.map((f, i) => (
                                    <span key={i} className="text-[10px] font-medium text-gray-500 bg-white px-1.5 py-0.5 rounded border border-gray-200">{String(f)}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )}
    </>
);

const FileText = ({ size, className }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
);

export default QuestionAnswerView;
